'use client';
import React, { useState } from 'react';
import { usePathname } from 'next/navigation';
import { SelectLanguage } from '@/features/select-language';
import { Box } from '../box';
import {
  ExpandIndicator,
  ExtraLargeLayout,
  LargeLayout,
  LargeSubLayout,
  LayoutAfter1280,
  LayoutAfter640,
  LayoutBefore1280,
  LayoutBefore640,
  LogoStyled,
  MiddleLayout,
  MiddleSubLayout,
  SmallLayout,
  StyledFooter,
  TextBlock,
  TextBlockExpandButton,
  Wrapper,
} from './styles';
import { useScopedI18n } from '@locales/client';
import { FooterContacts } from '@/shared/ui/footer/contacts';
import { AppsBlock } from '@/shared/ui/footer/appsBlock';
import { InfoBlock } from '@/shared/ui/footer/infoBlock';
import { CategoryBlock } from '@/shared/ui/footer/categoryBlock';
import { PaymentPartners } from '@/shared/ui/footer/paymentPartners';
import { BettingPartners } from '@/shared/ui/footer/bettingPartners';
import { BodyTextRegularB } from '@/shared/ui/typography2';
import { LicenseBlock } from '@/shared/ui/footer/licenseBlock';
import { ResponsibleGambling } from '@/shared/ui/footer/responsibleGambling';
import { fetcher } from '@/shared/api/fetcher';
import useSWR from 'swr';
import { withBreakpointsSlice } from '@/shared/providers/utils/with-breakpoints-slice';
import styled, { css } from 'styled-components';

const Footer = ({
  openWalletManagment,
  openCashbackModal,
  mobileLandscape,
}) => {
  const pathname = usePathname();
  const t = useScopedI18n('common.footer');
  const [textExpanded, setTextExpanded] = useState(false);

  const url = `/metadata?page=${pathname}`;
  const { data, isLoading, error } = useSWR(url, (url) => fetcher(url));

  return (
    <Wrapper>
      <StyledFooter>
        {!isLoading && !error && data?.pageText && (
          <TextBlock $textExpanded={textExpanded}>
            <HtmlContent
              $collapsed={!textExpanded}
              dangerouslySetInnerHTML={{ __html: data.pageText }}
            />
            <TextBlockExpandButton
              onClick={() => {
                setTextExpanded(!textExpanded);
              }}
            >
              <ExpandIndicator
                $expanded={textExpanded}
                src="/images/icons/indicator-lang.svg"
                width={20}
                height={20}
                alt="indicator"
              />
            </TextBlockExpandButton>
          </TextBlock>
        )}
        <LogoStyled />

        <SmallLayout>
          <FooterContacts />
          <AppsBlock />
          <InfoBlock />
          <CategoryBlock />
        </SmallLayout>
        <MiddleLayout>
          <MiddleSubLayout>
            <FooterContacts style={{ gridColumns: '1 / -1' }} />
            <AppsBlock />
          </MiddleSubLayout>
          <InfoBlock />
          <CategoryBlock />
        </MiddleLayout>
        <LargeLayout>
          <LargeSubLayout>
            <FooterContacts />
            <InfoBlock />
            <CategoryBlock />
          </LargeSubLayout>
          <AppsBlock />
        </LargeLayout>
        <ExtraLargeLayout>
          <FooterContacts />
          <InfoBlock />
          <CategoryBlock />
          <AppsBlock />
        </ExtraLargeLayout>

        <PaymentPartners openWalletManagment={openWalletManagment} />

        <LayoutBefore1280>
          <BettingPartners />
          <Box
            $display={'flex'}
            $gap={24}
            $width={'100%'}
            $alignContent={'stretch'}
            $alignItems={'stretch'}
          >
            <LicenseBlock />
            <ResponsibleGambling />
            <LayoutAfter640>
              <SelectLanguage $menuPlacement="top" />
            </LayoutAfter640>
          </Box>
          <LayoutBefore640>
            <SelectLanguage $menuPlacement="top" />
          </LayoutBefore640>
        </LayoutBefore1280>
        <LayoutAfter1280>
          <ResponsibleGambling />
          <LicenseBlock />
          <BettingPartners />
          <LayoutAfter640>
            <SelectLanguage $menuPlacement="top" />
          </LayoutAfter640>
        </LayoutAfter1280>
        <BodyTextRegularB
          style={{
            whiteSpace: 'pre-wrap',
            textAlign: mobileLandscape ? 'left' : 'center',
          }}
          color="#AFAFAF"
        >
          {t(mobileLandscape ? 'copyright_text_m' : 'copyright_text')}
        </BodyTextRegularB>
      </StyledFooter>
    </Wrapper>
  );
};

const FooterWithBreakpointsSlice = withBreakpointsSlice(
  Footer,
  ({ mobileLandscape }) => ({
    mobileLandscape,
  })
);

export { FooterWithBreakpointsSlice as Footer };

const HtmlContent = styled.div`
  display: flex;
  position: relative;
  width: 100%;
  flex-direction: column;
  align-items: flex-start;
  padding: 20px 20px 4px 20px;
  align-self: stretch;

  font-size: 14px;

  h1 {
    color: #fff;

    /* Desktop/Body Text/Medium/B */
    font-size: 16px;
    font-style: normal;
    font-weight: 500;
    line-height: 140%; /* 22.4px */

    margin-bottom: 1.5em;
  }

  ul,
  ol {
    list-style-position: inside;
    padding-left: 16px;
  }

  ul {
    padding-left: 16px;
  }

  h2,
  h3 {
    color: #fff;

    /* Desktop/Body Text/Medium/C */
    font-size: 14px;
    font-style: normal;
    font-weight: 500;
    line-height: 140%; /* 19.6px */

    margin-top: 1em;
    margin-bottom: 1em;
  }

  p {
    margin-bottom: 1em;
    line-height: 1.6;
  }

  p,
  li {
    color: #afafaf;

    font-size: 14px;
    font-style: normal;
    font-weight: 400;
    line-height: 140%; /* 19.6px */

    overflow-wrap: break-word;
    word-break: break-word;
    hyphens: auto;
  }

  ${({ $collapsed }) =>
    $collapsed &&
    css`
      & > p:nth-of-type(1) {
        display: block;
        display: -webkit-box;
        -webkit-line-clamp: 1;
        line-clamp: 1;
        -webkit-box-orient: vertical;
        overflow: hidden;
        text-overflow: ellipsis;
      }
    `}

  ol,
    ul {
    margin-bottom: 1.5em;
    padding-left: 1.5em;
  }

  li {
    margin-bottom: 0.5em;
  }
`;
